/**
 * Unified Registration Service
 * خدمة التسجيل الموحدة
 */

import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { linkingCodesService } from './linkingCodesService';
import {
  UnifiedRegistrationData,
  StudentRegistrationData,
  ParentRegistrationData,
  RegistrationResult,
  ERROR_MESSAGES,
} from '../types/registration';
import { validateRegistrationData } from './registrationValidation';
export { validateRegistrationData } from './registrationValidation';

// التحقق من توفر البريد الإلكتروني
export const checkEmailAvailability = async (
  _email: string
): Promise<boolean> =>
  // Firebase Auth هو مصدر الحقيقة لتكرار البريد عند إنشاء الحساب.
  true;

// الحصول على مدرس متاح للطالب الجديد
const getAvailableTeacher = async (): Promise<string> => {
  try {
    // البحث عن مدرس متاح (لديه أقل عدد من الطلاب أو المدرس الافتراضي)
    const teachersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'teacher'),
      limit(1)
    );

    const teachersSnapshot = await getDocs(teachersQuery);

    if (!teachersSnapshot.empty) {
      return teachersSnapshot.docs[0].id;
    }

    // إذا لم يوجد مدرس، استخدم معرف المدرس الافتراضي
    return '7GePvD5eWQP4zNL8pOmtMRaENfy1'; // teacher@clas.app
  } catch (error) {
    console.error('Error finding available teacher:', error);
    // في حالة الخطأ، استخدم المدرس الافتراضي
    return '7GePvD5eWQP4zNL8pOmtMRaENfy1';
  }
};

// تسجيل طالب جديد
const registerStudent = async (
  data: StudentRegistrationData
): Promise<RegistrationResult> => {
  try {
    // إنشاء حساب Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );
    const firebaseUser = userCredential.user;

    // تحديث الاسم في Firebase Auth
    await updateProfile(firebaseUser, { displayName: data.name });

    // التأكد من تحديث displayName
    await firebaseUser.reload();

    // الحصول على المدرس المناسب للطالب الجديد
    const teacherId = await getAvailableTeacher();

    // إنشاء كود فريد للطالب أولاً
    const codeResult = await linkingCodesService.createLinkingCode(
      firebaseUser.uid,
      firebaseUser.uid, // الطالب أنشأ الكود بنفسه
      {
        userType: 'student',
        codeType: 'permanent',
        metadata: {
          studentGrade: data.grade,
          schoolInfo: {
            teacherId,
            phone: data.phone,
          },
        },
      }
    );

    // إنشاء مستند legacy للتوافق مع الكود
    const legacyUserDoc = {
      id: firebaseUser.uid,
      email: data.email,
      name: data.name,
      phone: data.phone,
      role: 'student',
      linkingCode: codeResult.code, // إضافة الكود هنا أيضاً
      photoURL: firebaseUser.photoURL || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      studentProfile: {
        grade: data.grade,
        enrolledClasses: [],
        totalHours: 0,
        averageGrade: 0,
        pendingParentId: null as string | null,
      },
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), legacyUserDoc);

    let linkedUserId: string | undefined;
    const warnings: string[] = [];

    // ربط مع ولي الأمر إذا تم تقديم الكود
    if (data.parentCode) {
      try {
        const validation = await linkingCodesService.validateCode(
          data.parentCode
        );
        if (
          validation.isValid &&
          validation.linkingCode &&
          validation.linkingCode.userType === 'parent'
        ) {
          linkedUserId = validation.linkingCode.userId;
          warnings.push(
            'تم حفظ كود ولي الأمر. الربط التشغيلي يتم عبر طلب اعتماد داخل مساحة المدرس.'
          );
        } else {
          warnings.push(
            `لم يتم قبول كود ولي الأمر: ${
              validation.linkingCode?.userType && validation.linkingCode.userType !== 'parent'
                ? 'هذا الكود ليس كود ولي أمر'
                : validation.error
            }`
          );
        }
      } catch (error) {
        warnings.push(
          `خطأ في ربط الحساب مع ولي الأمر: ${
            error instanceof Error ? error.message : 'خطأ غير محدد'
          }`
        );
      }
    }

    return {
      success: true,
      accountCreated: true,
      codeGenerated: true,
      linkPendingApproval: Boolean(linkedUserId),
      userId: firebaseUser.uid,
      userType: 'student',
      linkedUserId,
      generatedCode: codeResult.code,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch (error: any) {
    console.error('Student registration error:', error);
    return {
      success: false,
      error: getRegistrationErrorMessage(error),
    };
  }
};

// تسجيل ولي أمر جديد
const registerParent = async (
  data: ParentRegistrationData
): Promise<RegistrationResult> => {
  try {
    // التحقق من صحة كود الطالب أولاً
    const codeValidation = await linkingCodesService.validateCode(
      data.studentCode
    );
    if (!codeValidation.isValid || !codeValidation.linkingCode) {
      return {
        success: false,
        error: codeValidation.error || ERROR_MESSAGES.invalidCode,
      };
    }

    const studentCodeData = codeValidation.linkingCode;
    if (studentCodeData.userType !== 'student') {
      return {
        success: false,
        error: 'هذا الكود خاص بالطالب فقط',
      };
    }

    // إنشاء حساب Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );
    const firebaseUser = userCredential.user;

    // تحديث الاسم في Firebase Auth
    await updateProfile(firebaseUser, { displayName: data.name });

    // إنشاء كود حقيقي لولي الأمر أولاً
    const parentLinkingCode = await linkingCodesService.createLinkingCode(
      firebaseUser.uid,
      firebaseUser.uid,
      {
        userType: 'parent',
        codeType: 'permanent',
        metadata: {
          userName: data.name,
          userEmail: data.email,
          userPhone: data.phone,
          linkedStudentId: studentCodeData.userId,
          createdAt: new Date(),
        },
      }
    );

    // إنشاء مستند legacy للتوافق مع الكود
    const legacyUserDoc = {
      id: firebaseUser.uid,
      email: data.email,
      name: data.name,
      phone: data.phone,
      role: 'parent',
      linkingCode: parentLinkingCode.code, // إضافة الكود هنا أيضاً
      photoURL: firebaseUser.photoURL || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      parentProfile: {
        children: [],
        pendingStudentId: studentCodeData.userId,
        pendingStudentCode: data.studentCode.trim().toUpperCase(),
      },
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), legacyUserDoc);

    // ربط الطالب مع ولي الأمر باستخدام الكود (معطل مؤقتاً)
    /*
    try {
      const linkResult = await linkWithStudentCode(
        data.studentCode,
        firebaseUser.uid
      );

      if (!linkResult.success) {
        console.warn(
          'Failed to link student after parent registration:',
          linkResult.error
        );
      }
    } catch (error) {
      console.warn(
        'Error linking student after parent registration:',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
    */

    return {
      success: true,
      accountCreated: true,
      codeGenerated: true,
      linkPendingApproval: true,
      userId: firebaseUser.uid,
      userType: 'parent',
      linkedUserId: studentCodeData.userId,
      generatedCode: parentLinkingCode.code,
    };
  } catch (error: any) {
    console.error('Parent registration error:', error);
    return {
      success: false,
      error: getRegistrationErrorMessage(error),
    };
  }
};

// الدالة الرئيسية للتسجيل الموحد
export const registerUnifiedUser = async (
  data: UnifiedRegistrationData
): Promise<RegistrationResult> => {
  try {
    // التحقق من صحة البيانات
    const validationErrors = validateRegistrationData(data);
    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.values(validationErrors)[0];
      return {
        success: false,
        error: firstError,
      };
    }

    // Firebase Auth سيتولى فحص توفر البريد الإلكتروني تلقائياً

    // التسجيل حسب نوع المستخدم
    if (data.userType === 'student') {
      return await registerStudent(data as StudentRegistrationData);
    } else if (data.userType === 'parent') {
      return await registerParent(data as ParentRegistrationData);
    } else {
      return {
        success: false,
        error: 'نوع المستخدم غير صحيح',
      };
    }
  } catch (error: any) {
    console.error('Unified registration error:', error);
    return {
      success: false,
      error: getRegistrationErrorMessage(error),
    };
  }
};

// تحويل أخطاء Firebase إلى رسائل مفهومة
const getRegistrationErrorMessage = (error: any): string => {
  if (error?.code) {
    switch (error.code) {
      case 'auth/email-already-in-use':
        return ERROR_MESSAGES.emailExists;
      case 'auth/invalid-email':
        return ERROR_MESSAGES.invalidEmail;
      case 'auth/weak-password':
        return ERROR_MESSAGES.passwordTooShort;
      case 'auth/network-request-failed':
        return ERROR_MESSAGES.networkError;
      default:
        return ERROR_MESSAGES.unknownError;
    }
  }

  return error?.message || ERROR_MESSAGES.unknownError;
};
